import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Award, Heart, Shield, GraduationCap } from 'lucide-react';
import { aboutInfo } from '../data/mockData';

export default function AProposPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-neutral-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
              {aboutInfo.title}
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              {aboutInfo.introduction}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-200 to-amber-100 rounded-2xl opacity-20 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/5207117/pexels-photo-5207117.jpeg"
                alt="Infirmière professionnelle"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
                style={{ aspectRatio: '3/4' }}
              />
              
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <Card className="border-amber-200 bg-gradient-to-br from-white to-amber-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-700">10+</p>
                    <p className="text-sm text-neutral-600">Années d'expérience</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-gradient-to-br from-white to-amber-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-700">100%</p>
                    <p className="text-sm text-neutral-600">Conventionnée INAMI</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              {/* Experience */}
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">Mon parcours</h2>
                <p className="text-neutral-600 leading-relaxed mb-4">
                  {aboutInfo.experience}
                </p>
                <p className="text-neutral-600 leading-relaxed">
                  {aboutInfo.approach}
                </p>
              </div>

              {/* Education */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <GraduationCap className="h-5 w-5 text-amber-700" />
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900">Formation et diplômes</h3>
                </div>
                <ul className="space-y-2">
                  {aboutInfo.education.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-amber-600 mr-3 mt-1">•</span>
                      <span className="text-neutral-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Mes valeurs professionnelles
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Les principes qui guident ma pratique quotidienne des soins infirmiers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {aboutInfo.values.map((value, index) => {
              const icons = [Award, Heart, Shield];
              const IconComponent = icons[index] || Award;
              
              return (
                <Card key={index} className="border-neutral-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-amber-700" />
                    </div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-amber-200 shadow-lg">
            <CardContent className="p-8 lg:p-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-neutral-900 mb-6 text-center">
                Mon approche du soin à domicile
              </h2>
              
              <div className="space-y-6 text-neutral-700 leading-relaxed">
                <p>
                  Le soin à domicile représente bien plus qu'un simple acte technique. C'est une démarche 
                  qui s'inscrit dans le respect de votre environnement familier, de vos habitudes de vie 
                  et de votre intimité.
                </p>
                
                <p>
                  Ma pratique repose sur une écoute attentive de vos besoins et de ceux de votre famille. 
                  Chaque patient est unique, et les soins doivent s'adapter à votre situation particulière, 
                  vos contraintes et vos préférences.
                </p>
                
                <p>
                  Je travaille en collaboration étroite avec votre médecin traitant et les autres 
                  professionnels de santé qui vous accompagnent. Cette coordination garantit une prise 
                  en charge cohérente et optimale de votre état de santé.
                </p>
                
                <p className="font-medium text-neutral-900">
                  L'objectif : vous permettre de vivre chez vous dans les meilleures conditions, 
                  en toute sécurité et avec un maximum de confort.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Professional Commitments */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-neutral-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Mes engagements professionnels
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-neutral-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-6 w-6 text-amber-700" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">Secret professionnel</h3>
                <p className="text-sm text-neutral-600">
                  Confidentialité totale de vos informations médicales
                </p>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-6 w-6 text-amber-700" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">Qualité des soins</h3>
                <p className="text-sm text-neutral-600">
                  Respect strict des protocoles et bonnes pratiques
                </p>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-amber-700" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">Bienveillance</h3>
                <p className="text-sm text-neutral-600">
                  Approche humaine et empathique envers chaque patient
                </p>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="h-6 w-6 text-amber-700" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">Formation continue</h3>
                <p className="text-sm text-neutral-600">
                  Mise à jour régulière de mes compétences
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-r from-amber-600 to-amber-700 border-0">
            <CardContent className="p-8 lg:p-12 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Faisons connaissance
              </h2>
              <p className="text-xl text-amber-50 mb-8">
                Discutons de vos besoins en soins infirmiers lors d'un premier contact
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-amber-700 hover:bg-amber-50">
                  Me contacter
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
